import * as BaseApi from "api/Base.api";
import { IPagination, ILikeToggleResponse, IBookmarkToggleResponse } from "api/interfaces/common.interface";
import { IDiary, IDiaryComment, IDiaryLike } from "api/interfaces/profile.interface";
import { IUser } from "api/interfaces/user.interface";

export interface IDiaryListResult {
    diaryList: IDiary[];
    pagination: IPagination;
}

export interface IDiaryDetailResult {
    diary: IDiary;
}

export interface IDiaryCommentsResult {
    commentList: IDiaryComment[];
    pagination: IPagination;
}

export interface ICreateCommentRequest {
    contents: string;
    parentId?: number;
}

export interface ICreateCommentResponse {
    comment: IDiaryComment;
}

export interface IUpdateCommentRequest {
    contents: string;
}

export interface IDiaryLikesResult {
    likeList: IDiaryLike[];
    pagination: IPagination;
}

// 예시 데이터 형태:
// {
//   "2024-01-15 14:30": [123, 456, 789],
//   "2024-01-15 14:31": [234, 567],
//   "2024-01-15 14:32": [345, 678, 901]
// }
export interface IDiaryViewLogGroupRequest {
    [dateKey: string]: number[];
}

export interface IDiaryViewLogResponse {
    success: boolean;
    message?: string;
}

export interface IBookmarkedDiariesResult {
    diaryList: IDiary[];
    pagination: IPagination;
}

// 지망생 다이어리 목록 조회
export const getDiaryList = async (params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    userStudentId?: number;
}): Promise<IDiaryListResult> => {
    try {
        const data = await BaseApi.get("/agency/v2/diary", params);
        console.log("지망생 다이어리 목록 조회 응답:", { data });
        return data as IDiaryListResult;
    } catch (error) {
        console.error("지망생 다이어리 목록 조회 실패:", error);
        throw error;
    }
};

// 지망생 다이어리 상세 조회
export const getDiaryDetail = async (diaryId: string): Promise<IDiaryDetailResult> => {
    try {
        const response = await BaseApi.get(`/agency/v2/diary/${diaryId}`);
        return response as IDiaryDetailResult;
    } catch (error) {
        console.error("지망생 다이어리 상세 조회 실패:", error);
        throw error;
    }
};

// 다이어리 좋아요 토글
export const toggleDiaryLike = async (diaryId: string): Promise<ILikeToggleResponse> => {
    try {
        const response = await BaseApi.post2(`/agency/v2/diary/${diaryId}/like`);
        return response as ILikeToggleResponse;
    } catch (error) {
        console.error("다이어리 좋아요 토글 실패:", error);
        throw error;
    }
};

// 다이어리 좋아요 목록 조회
export const getDiaryLikes = async (diaryId: string, params?: {
    page?: number;
    limit?: number;
}): Promise<IDiaryLikesResult> => {
    try {
        const response = await BaseApi.get(`/agency/v2/diary/${diaryId}/like`, params);
        return response as IDiaryLikesResult;
    } catch (error) {
        console.error("다이어리 좋아요 목록 조회 실패:", error);
        throw error;
    }
};

// 다이어리 댓글 목록 조회
export const getDiaryComments = async (diaryId: string, params?: {
    page?: number;
    limit?: number;
}): Promise<IDiaryCommentsResult> => {
    try {
        const response = await BaseApi.get(`/agency/v2/diary/${diaryId}/comment`, params);
        return response as IDiaryCommentsResult;
    } catch (error) {
        console.error("다이어리 댓글 목록 조회 실패:", error);
        throw error;
    }
};

// 다이어리에 댓글 작성
export const createDiaryComment = async (diaryId: string, content: string, parentId?: number): Promise<ICreateCommentResponse> => {
    try {
        const data: ICreateCommentRequest = {
            contents: content,
            parentId: parentId
        };
        const response = await BaseApi.post2(`/agency/v2/diary/${diaryId}/comment`, data);
        return response as ICreateCommentResponse;
    } catch (error) {
        console.error("다이어리 댓글 작성 실패:", error);
        throw error;
    }
};

// 댓글 수정
export const updateDiaryComment = async (diaryId: string, commentId: number, content: string): Promise<void> => {
    try {
        const data: IUpdateCommentRequest = {
            contents: content
        };
        await BaseApi.put(`/agency/v2/diary/${diaryId}/comment/${commentId}`, data);
    } catch (error) {
        console.error("댓글 수정 실패:", error);
        throw error;
    }
};

// 댓글 삭제
export const deleteDiaryComment = async (diaryId: string, commentId: number): Promise<void> => {
    try {
        await BaseApi.remove(`/agency/v2/diary/${diaryId}/comment/${commentId}`);
    } catch (error) {
        console.error("댓글 삭제 실패:", error);
        throw error;
    }
};

// 댓글 좋아요 토글
export const toggleCommentLike = async (diaryId: string, commentId: number): Promise<ILikeToggleResponse> => {
    try {
        const response = await BaseApi.post2(`/agency/v2/diary/${diaryId}/comment/${commentId}/like`);
        return response as ILikeToggleResponse;
    } catch (error) {
        console.error("댓글 좋아요 토글 실패:", error);
        throw error;
    }
};

// 다이어리 통계 조회 (선택적)
export const getDiaryStats = async (params?: {
    startDate?: string;
    endDate?: string;
    studentId?: string;
}) => {
    try {
        const response = await BaseApi.get("/agency/v2/diary/stats", params);
        return response;
    } catch (error) {
        console.error("다이어리 통계 조회 실패:", error);
        throw error;
    }
};

//* 다이어리 뷰 로그 저장
//  2025-08-28 
//  희문
//* Params
//  loggedAt:Date
//  diaryIdList: number[]
//  isOnList: boolean
export const saveDiaryViewLogGrouped = async (params: IDiaryViewLogGroupRequest): Promise<IDiaryViewLogResponse> => {
    try {
        const response = await BaseApi.post2("/agency/v2/diary/log/view/grouped", params);
        console.log("다이어리 뷰 로그 저장 성공:", response);
        return response as IDiaryViewLogResponse;
    } catch (error) {
        console.error("다이어리 뷰 로그 저장 실패:", error);
        throw error;
    }
};

//* 다이어리 북마크 토글
//  2025-01-27
//  희문
//* Params
//  diaryId: string
export const toggleDiaryBookmark = async (diaryId: string): Promise<IBookmarkToggleResponse> => {
    try {
        const response = await BaseApi.post2(`/agency/v2/diary/${diaryId}/bookmark`);
        console.log("다이어리 북마크 토글 성공:", response);
        return response as IBookmarkToggleResponse;
    } catch (error) {
        console.error("다이어리 북마크 토글 실패:", error);
        throw error;
    }
};

//* 북마크한 다이어리 목록 조회
//  2025-01-27
//  희문
//* Params
//  page: number
export const getBookmarkedDiaries = async (page: number): Promise<IBookmarkedDiariesResult> => {
    try {
        const response = await BaseApi.get("/agency/v2/diary/bookmark/list", { page });
        console.log("북마크한 다이어리 목록 조회 성공:", response);
        return response as IBookmarkedDiariesResult;
    } catch (error) {
        console.error("북마크한 다이어리 목록 조회 실패:", error);
        throw error;
    }
};

export interface ITranslateResponse {
    translatedTitle: string;
    translatedContent: string;
}

export interface ICommentTranslateResponse {
    translatedContents: string;
}

//* 다이어리 번역
//  2025-01-27
//  희문
//* Params
//  diaryId: string
export const translateDiary = async (diaryId: string): Promise<ITranslateResponse> => {
    try {
        const response = await BaseApi.post2(`/agency/v2/diary/${diaryId}/translate`);
        console.log("다이어리 번역 성공:", response);
        return response as ITranslateResponse;
    } catch (error) {
        console.error("다이어리 번역 실패:", error);
        throw error;
    }
};

//* 다이어리 코멘트 번역
//  2025-01-27
//  희문
//* Params
//  diaryId: string
//  commentId: string
export const translateDiaryComment = async (diaryId: string, commentId: string): Promise<ICommentTranslateResponse> => {
    try {
        const response = await BaseApi.post2(`/agency/v2/diary/${diaryId}/comment/${commentId}/translate`);
        console.log("다이어리 코멘트 번역 성공:", response);
        return response as ICommentTranslateResponse;
    } catch (error) {
        console.error("다이어리 코멘트 번역 실패:", error);
        throw error;
    }
};
